/**
 * Orders, and the queue a kitchen works from.
 *
 * Network-only. A kitchen screen that kept a local copy would keep showing an
 * order somebody else had already handed over, and two people would make the
 * same kota — which is the exact failure the screen exists to prevent.
 */

import { api } from "@/api/client";

export type OrderStatus = "NEW" | "PREPARING" | "READY" | "HANDED_OVER" | "CANCELLED";
export type OrderType = "TAKEAWAY" | "EAT_IN" | "COLLECTION" | "DELIVERY";

export interface OrderLineModifier {
  id: string;
  name: string;
  price_delta: string;
}

export interface OrderLine {
  id: string;
  product: string;
  description: string;
  quantity: string;
  unit_price: string;
  line_total: string;
  modifiers: OrderLineModifier[];
  note: string;
}

export interface Order {
  id: string;
  number: number;
  business_date: string;
  order_type: OrderType;
  status: OrderStatus;
  customer_name: string;
  customer_phone: string;
  customer: string | null;
  note: string;
  total: string;
  promised_for: string | null;
  /** What somebody handing over a bag needs to know before they let go of it. */
  is_paid: boolean;
  receipt_number: string;
  placed_by_name: string | null;
  cancelled_reason: string;
  lines: OrderLine[];
  status_log: {
    id: string;
    from_status: string;
    to_status: string;
    note: string;
    changed_by_name: string | null;
    created_at: string;
  }[];
  created_at: string;
  updated_at: string;
}

export interface PlaceOrderLine {
  product_id: string;
  quantity: string;
  modifier_ids?: string[];
  note?: string;
}

interface Page<T> {
  count: number;
  next: string | null;
  results: T[];
}

function unwrap<T>(payload: Page<T> | T[]): T[] {
  return Array.isArray(payload) ? payload : payload.results;
}

export const ordersRepository = {
  /** Everything a ticket needs, in one call. */
  kitchen: (sectionId?: string) =>
    api.get<Order[]>(`/orders/kitchen/${sectionId ? `?section=${sectionId}` : ""}`),

  list: async (params: { status?: string; on?: string; open?: boolean } = {}): Promise<Order[]> => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.on) query.set("on", params.on);
    if (params.open) query.set("open", "true");
    return unwrap(await api.get<Page<Order> | Order[]>(`/orders/?${query.toString()}`));
  },

  place: (body: {
    section_id: string;
    lines: PlaceOrderLine[];
    order_type?: OrderType;
    customer_name?: string;
    customer_phone?: string;
    note?: string;
    promised_for?: string | null;
    sale_id?: string | null;
  }) => api.post<Order>("/orders/", body),

  setStatus: (id: string, status: OrderStatus, note = "") =>
    api.post<Order>(`/orders/${id}/status/`, { status, note }),

  cancel: (id: string, reason: string) => api.post<Order>(`/orders/${id}/cancel/`, { reason }),

  attachSale: (id: string, saleId: string) =>
    api.post<Order>(`/orders/${id}/attach-sale/`, { sale_id: saleId }),
};
